import { defineStore } from 'pinia'
import { ref, computed, readonly } from 'vue'
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged
} from 'firebase/auth'
import type { User as FirebaseUser } from 'firebase/auth'
import { auth } from '@/services/firebase'
import { apiClient } from '@/services/api/base'
import { authDebug } from '@/utils/authDebug'
import type { User } from '@/types'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const isLoading = ref(true)
  const error = ref<string | null>(null)
  const isInitialized = ref(false)

  const isAuthenticated = computed(() => !!user.value)

  const setUser = async (firebaseUser: FirebaseUser | null) => {
    if (firebaseUser) {
      authDebug.log('Setting authenticated user', {
        uid: firebaseUser.uid,
        email: firebaseUser.email
      })

      user.value = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL
      }

      // Obter e definir o token de autenticação
      try {
        const token = await firebaseUser.getIdToken()
        apiClient.setAuthToken(token)
        await authDebug.logTokenInfo(token)
      } catch (err) {
        authDebug.error('Erro ao obter token do Firebase', err)
      }
    } else {
      authDebug.log('Clearing user (logout)')
      user.value = null
      apiClient.removeAuthToken()
    }
    isLoading.value = false
    isInitialized.value = true
  }

  const signIn = async (email: string, password: string) => {
    try {
      error.value = null
      isLoading.value = true
      await signInWithEmailAndPassword(auth, email, password)
    } catch (err: any) {
      error.value = err.message || 'Erro ao fazer login'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const signUp = async (email: string, password: string) => {
    try {
      error.value = null
      isLoading.value = true
      await createUserWithEmailAndPassword(auth, email, password)
    } catch (err: any) {
      error.value = err.message || 'Erro ao criar conta'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const signOut = async () => {
    try {
      authDebug.log('🚪 Iniciando logout...')

      // Marcar como logout explícito
      sessionStorage.setItem('explicit_logout', 'true')

      // Limpar estado do store primeiro
      user.value = null
      isLoading.value = false
      isInitialized.value = true
      error.value = null

      // Limpar token da API
      apiClient.removeAuthToken()

      // Limpar dados SSO
      localStorage.removeItem('sso_authenticated')
      localStorage.removeItem('sso_auth_data')
      localStorage.removeItem('firebase_token')

      // Logout do Firebase (pode falhar se não logado via Firebase)
      try {
        await firebaseSignOut(auth)
        authDebug.log('� Firebase logout realizado')
      } catch (firebaseErr) {
        authDebug.log('⚠️ Firebase logout falhou (normal para SSO)', firebaseErr)
      }

      authDebug.log('✅ Logout completo - todos os dados limpos')
    } catch (err: any) {
      authDebug.error('❌ Erro durante logout', err)
      error.value = err.message || 'Erro ao fazer logout'
      throw err
    }
  }

  const initAuth = () => {
    return new Promise<void>(resolve => {
      // 🔍 Verificar se há dados SSO válidos no localStorage
      const checkSSOAuth = () => {
        const ssoAuth = localStorage.getItem('sso_authenticated')
        const ssoData = localStorage.getItem('sso_auth_data')
        const token = localStorage.getItem('firebase_token')

        if (ssoAuth === 'true' && ssoData && token) {
          try {
            const authData = JSON.parse(ssoData)
            // Verificar se não expirou (24 horas)
            if (Date.now() - authData.timestamp < 24 * 60 * 60 * 1000) {
              authDebug.log('🔄 Restaurando autenticação SSO do localStorage', authData.user)

              user.value = authData.user
              isLoading.value = false
              isInitialized.value = true

              // Configurar token na API
              apiClient.setAuthToken(token)

              authDebug.log('✅ Autenticação SSO restaurada com sucesso')
              resolve()
              return true
            } else {
              authDebug.log('⏰ Dados SSO expirados, limpando...')
            }
          } catch (err) {
            authDebug.error('❌ Erro ao parsear dados SSO', err)
          }

          // Limpar dados inválidos/expirados
          localStorage.removeItem('sso_authenticated')
          localStorage.removeItem('sso_auth_data')
          localStorage.removeItem('firebase_token')
        }
        return false
      }

      // Tentar restaurar SSO primeiro
      if (checkSSOAuth()) {
        return // SSO restaurado com sucesso
      }

      // Fluxo normal do Firebase se não há SSO
      const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
        await setUser(firebaseUser)
        resolve()
        unsubscribe() // Limpa o listener após a primeira execução
      })
    })
  }

  // Função para manter o listener de mudanças de autenticação ativo
  const startAuthListener = () => {
    onAuthStateChanged(auth, async firebaseUser => {
      // Se não há usuário do Firebase, verificar se há SSO ativo
      if (!firebaseUser) {
        const ssoAuth = localStorage.getItem('sso_authenticated')
        const ssoData = localStorage.getItem('sso_auth_data')
        const token = localStorage.getItem('firebase_token')

        if (ssoAuth === 'true' && ssoData && token) {
          try {
            const authData = JSON.parse(ssoData)
            // Verificar se não expirou (24 horas)
            if (Date.now() - authData.timestamp < 24 * 60 * 60 * 1000) {
              authDebug.log('🔄 Mantendo autenticação SSO ativa')
              user.value = authData.user
              apiClient.setAuthToken(token)
              isLoading.value = false
              isInitialized.value = true
              return // Manter SSO ativo
            } else {
              // Limpar dados expirados
              localStorage.removeItem('sso_authenticated')
              localStorage.removeItem('sso_auth_data')
              localStorage.removeItem('firebase_token')
              authDebug.log('⏰ SSO expirado, limpando dados')
            }
          } catch (err) {
            authDebug.error('❌ Erro ao manter SSO', err)
          }
        }
      }

      // Fluxo normal do Firebase
      await setUser(firebaseUser)
    })
  }

  const clearError = () => {
    error.value = null
  }

  const setError = (message: string) => {
    error.value = message
  }

  // Função para verificar e manter autenticação SSO
  const checkAndMaintainSSO = () => {
    // Se já está autenticado, não precisa verificar
    if (isAuthenticated.value) {
      return true
    }

    // Se foi feito logout explícito, não restaurar
    const logoutFlag = sessionStorage.getItem('explicit_logout')
    if (logoutFlag === 'true') {
      authDebug.log('🚪 Logout explícito detectado - não restaurando SSO')
      return false
    }

    const ssoAuth = localStorage.getItem('sso_authenticated')
    const ssoData = localStorage.getItem('sso_auth_data')
    const token = localStorage.getItem('firebase_token')

    if (ssoAuth === 'true' && ssoData && token) {
      try {
        const authData = JSON.parse(ssoData)
        // Verificar se não expirou (24 horas)
        if (Date.now() - authData.timestamp < 24 * 60 * 60 * 1000) {
          authDebug.log('🔄 Restaurando SSO durante navegação')
          user.value = authData.user
          apiClient.setAuthToken(token)
          isLoading.value = false
          isInitialized.value = true
          return true
        } else {
          // Limpar dados expirados
          localStorage.removeItem('sso_authenticated')
          localStorage.removeItem('sso_auth_data')
          localStorage.removeItem('firebase_token')
          authDebug.log('⏰ SSO expirado durante verificação')
        }
      } catch (err) {
        authDebug.error('❌ Erro ao verificar SSO', err)
        // Limpar dados corrompidos
        localStorage.removeItem('sso_authenticated')
        localStorage.removeItem('sso_auth_data')
        localStorage.removeItem('firebase_token')
      }
    }

    return false
  }

  const refreshToken = async () => {
    const currentUser = auth.currentUser
    if (currentUser) {
      try {
        authDebug.log('Refreshing token...')
        const token = await currentUser.getIdToken(true) // force refresh
        apiClient.setAuthToken(token)
        await authDebug.logTokenInfo(token)
        authDebug.log('Token refreshed successfully')
        return token
      } catch (err) {
        authDebug.error('Erro ao renovar token', err)
        throw err
      }
    }
    return null
  }

  // Auto refresh token periodicamente
  const startTokenRefresh = () => {
    // Refresh token a cada 50 minutos (tokens do Firebase expiram em 1 hora)
    setInterval(
      async () => {
        if (isAuthenticated.value) {
          try {
            await refreshToken()
            authDebug.log('Token refreshed automatically')
          } catch (error) {
            authDebug.error('Erro ao renovar token automaticamente', error)
          }
        }
      },
      50 * 60 * 1000
    ) // 50 minutos
  }

  return {
    user: readonly(user),
    isLoading: readonly(isLoading),
    error: readonly(error),
    isInitialized: readonly(isInitialized),
    isAuthenticated,
    setUser, // Expor para uso em SSO
    signIn,
    signUp,
    signOut,
    initAuth,
    startAuthListener,
    clearError,
    setError,
    refreshToken,
    startTokenRefresh,
    checkAndMaintainSSO // Expor para router guard
  }
})
